// Self-contained fullscreen lightbox for the dw-product gallery.
// Mouse drag + wheel/trackpad zoom + double-click on desktop;
// pinch + drag + double-tap + horizontal swipe on touch;
// ESC + ArrowLeft/ArrowRight for keyboard.

(() => {
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const DOUBLE_ZOOM = 2.5;
  const DRAG_THRESHOLD = 6;
  const SWIPE_THRESHOLD = 60;
  const DOUBLE_TAP_MS = 300;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  /**
   * Initialize the lightbox for a single dw-product section.
   * @param {HTMLElement} section
   */
  function initSection(section) {
    if (section.__dwLightboxInit) return;
    section.__dwLightboxInit = true;

    const trigger = section.querySelector('[data-main-image-container]');
    const mainImg = section.querySelector('[data-main-image]');
    const thumbs = Array.from(section.querySelectorAll('[data-thumbnail]'));

    if (!trigger || !mainImg) return;

    // Videos, external videos and 3D models are played inline in the gallery --
    // the zoom/pan stage only understands images, so they are excluded here.
    const imageThumbs = thumbs.filter((t) => (t.dataset.mediaType || 'image') === 'image');

    // Maps a gallery index (position in the full media list) to its position in
    // `images`, so the lightbox opens on the media the shopper is looking at.
    /** @type {Map<number, number>} */
    const galleryToLightbox = new Map();
    imageThumbs.forEach((t, i) => {
      const galleryIndex = parseInt(t.dataset.index, 10);
      if (!isNaN(galleryIndex)) galleryToLightbox.set(galleryIndex, i);
    });

    /** @type {{src: string, srcset?: string, alt: string}[]} */
    const images = imageThumbs.length
      ? imageThumbs.map((t) => ({
          src: t.dataset.imageUrl || t.querySelector('img')?.src || '',
          srcset: t.dataset.imageSrcset,
          alt: t.dataset.imageAlt || '',
        }))
      : [{ src: mainImg.currentSrc || mainImg.src, srcset: mainImg.srcset, alt: mainImg.alt }];

    if (!images.length) return;

    /**
     * Resolve the lightbox slide for the currently displayed gallery media.
     * Returns null when the active media isn't an image (video / model), which
     * means the lightbox should stay closed.
     * @returns {number|null}
     */
    function lightboxIndexForCurrentMedia() {
      const idx = section.querySelector('[data-current-index]');
      const galleryIndex = idx ? parseInt(idx.textContent, 10) - 1 : 0;
      if (isNaN(galleryIndex)) return 0;
      if (!galleryToLightbox.size) return 0;
      const mapped = galleryToLightbox.get(galleryIndex);
      return mapped === undefined ? null : mapped;
    }

    const single = images.length <= 1;

    // Build the lightbox DOM once per section.
    const overlay = document.createElement('div');
    overlay.className = 'dw-product-lightbox';
    if (single) overlay.dataset.single = '';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Product image viewer');
    overlay.innerHTML = `
      <button type="button" class="dw-product-lightbox__btn dw-product-lightbox__close" aria-label="Close">
        <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <button type="button" class="dw-product-lightbox__btn dw-product-lightbox__prev" aria-label="Previous image">
        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
      </button>
      <button type="button" class="dw-product-lightbox__btn dw-product-lightbox__next" aria-label="Next image">
        <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="9 6 15 12 9 18"/></svg>
      </button>
      <div class="dw-product-lightbox__counter" aria-live="polite"><span data-current>1</span> / <span>${images.length}</span></div>
      <div class="dw-product-lightbox__hint">Scroll to zoom · drag to pan · double-click to toggle · arrows to navigate · Esc to close</div>
      <div class="dw-product-lightbox__stage">
        <img class="dw-product-lightbox__img" alt="" draggable="false" />
      </div>
    `;
    document.body.appendChild(overlay);

    const stage = overlay.querySelector('.dw-product-lightbox__stage');
    const img = overlay.querySelector('.dw-product-lightbox__img');
    const counterCurrent = overlay.querySelector('[data-current]');
    const closeBtn = overlay.querySelector('.dw-product-lightbox__close');
    const prevBtn = overlay.querySelector('.dw-product-lightbox__prev');
    const nextBtn = overlay.querySelector('.dw-product-lightbox__next');

    let currentIndex = 0;
    let scale = 1;
    let tx = 0;
    let ty = 0;

    function applyTransform() {
      img.style.setProperty('--lb-scale', String(scale));
      img.style.setProperty('--lb-tx', `${tx}px`);
      img.style.setProperty('--lb-ty', `${ty}px`);
    }

    function resetZoom() {
      scale = 1;
      tx = 0;
      ty = 0;
      applyTransform();
    }

    function constrain() {
      // Keep the image roughly within its visible bounds when zoomed.
      const rect = img.getBoundingClientRect();
      // After zoom, max translate is (scaledSize - viewportSize)/2.
      // Since we apply translate before scale (in CSS order), divide by scale.
      const maxX = Math.max(0, (rect.width - stage.clientWidth) / 2 / scale);
      const maxY = Math.max(0, (rect.height - stage.clientHeight) / 2 / scale);
      tx = clamp(tx, -maxX, maxX);
      ty = clamp(ty, -maxY, maxY);
    }

    function setIndex(i) {
      currentIndex = (i + images.length) % images.length;
      const data = images[currentIndex];
      img.src = data.src;
      if (data.srcset) img.srcset = data.srcset;
      else img.removeAttribute('srcset');
      img.alt = data.alt;
      counterCurrent.textContent = String(currentIndex + 1);
      resetZoom();
    }

    function open(i = 0) {
      setIndex(i);
      overlay.dataset.open = '';
      document.body.classList.add('dw-lightbox-open');
      // Focus close button so ESC works immediately.
      closeBtn.focus({ preventScroll: true });
    }

    function close() {
      delete overlay.dataset.open;
      document.body.classList.remove('dw-lightbox-open');
      resetZoom();
      trigger.focus({ preventScroll: true });
    }

    // ---- Trigger ---------------------------------------------------------
    trigger.style.cursor = 'zoom-in';
    trigger.setAttribute('data-lightbox-trigger', '');
    trigger.setAttribute('role', 'button');
    trigger.setAttribute('tabindex', '0');
    trigger.setAttribute('aria-label', 'Open image in fullscreen viewer');

    trigger.addEventListener('click', (e) => {
      // Don't open if user clicked one of the in-image nav arrows / counter.
      if (e.target.closest('[data-gallery-prev], [data-gallery-next], [data-gallery-counter]')) return;
      // Clicks on a video's controls or a 3D model belong to that player.
      if (e.target.closest('[data-media-slide]')) return;
      const i = lightboxIndexForCurrentMedia();
      if (i === null) return;
      open(i);
    });

    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (e.target.closest('[data-media-slide]')) return;
        const i = lightboxIndexForCurrentMedia();
        if (i === null) return;
        e.preventDefault();
        open(i);
      }
    });

    // ---- Buttons ---------------------------------------------------------
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', () => setIndex(currentIndex - 1));
    nextBtn.addEventListener('click', () => setIndex(currentIndex + 1));

    // Click on backdrop (not on image / buttons) closes.
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay || e.target === stage) close();
    });

    // ---- Keyboard --------------------------------------------------------
    document.addEventListener('keydown', (e) => {
      if (!('open' in overlay.dataset)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIndex(currentIndex + 1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex(currentIndex - 1);
      } else if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        zoomTo(scale * 1.2, stage.clientWidth / 2, stage.clientHeight / 2);
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomTo(scale / 1.2, stage.clientWidth / 2, stage.clientHeight / 2);
      }
    });

    // ---- Wheel / trackpad pinch zoom ------------------------------------
    stage.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        // Trackpad pinch arrives as wheel + ctrlKey, with bigger deltas.
        const sensitivity = e.ctrlKey ? 0.012 : 0.0025;
        const factor = Math.exp(-e.deltaY * sensitivity);
        const rect = stage.getBoundingClientRect();
        zoomTo(scale * factor, e.clientX - rect.left, e.clientY - rect.top);
      },
      { passive: false }
    );

    function zoomTo(targetScale, originX, originY) {
      const old = scale;
      const next = clamp(targetScale, MIN_ZOOM, MAX_ZOOM);
      if (next === old) return;
      const cx = stage.clientWidth / 2;
      const cy = stage.clientHeight / 2;
      const dx = originX - cx;
      const dy = originY - cy;
      const delta = next / old - 1;
      tx -= (dx * delta) / next;
      ty -= (dy * delta) / next;
      scale = next;
      if (scale <= MIN_ZOOM + 0.001) {
        tx = 0;
        ty = 0;
        scale = MIN_ZOOM;
      } else {
        constrain();
      }
      applyTransform();
    }

    // ---- Double click ----------------------------------------------------
    stage.addEventListener('dblclick', (e) => {
      e.preventDefault();
      const rect = stage.getBoundingClientRect();
      if (scale > MIN_ZOOM + 0.05) {
        scale = MIN_ZOOM;
        tx = 0;
        ty = 0;
        applyTransform();
      } else {
        zoomTo(DOUBLE_ZOOM, e.clientX - rect.left, e.clientY - rect.top);
      }
    });

    // ---- Mouse drag ------------------------------------------------------
    let mouseDragStart = null;
    let mouseDragTranslate = null;
    let mouseDragged = false;

    stage.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      mouseDragStart = { x: e.clientX, y: e.clientY };
      mouseDragTranslate = { x: tx, y: ty };
      mouseDragged = false;
      stage.classList.add('is-interacting');
    });

    window.addEventListener('mousemove', (e) => {
      if (!mouseDragStart) return;
      const dx = e.clientX - mouseDragStart.x;
      const dy = e.clientY - mouseDragStart.y;
      if (!mouseDragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      mouseDragged = true;
      stage.classList.add('is-dragging');
      if (scale <= MIN_ZOOM) return;
      tx = mouseDragTranslate.x + dx / scale;
      ty = mouseDragTranslate.y + dy / scale;
      constrain();
      applyTransform();
    });

    window.addEventListener('mouseup', () => {
      if (!mouseDragStart) return;
      mouseDragStart = null;
      mouseDragTranslate = null;
      stage.classList.remove('is-dragging');
      stage.classList.remove('is-interacting');
      // Reset mouseDragged on next tick so the click handler can read it.
      setTimeout(() => {
        mouseDragged = false;
      }, 0);
    });

    // Prevent click-to-close after a drag.
    stage.addEventListener(
      'click',
      (e) => {
        if (mouseDragged || scale > MIN_ZOOM + 0.05) {
          e.stopPropagation();
        }
      },
      { capture: true }
    );

    // ---- Touch (pinch + drag + swipe + double-tap) ----------------------
    /** @type {{x:number,y:number}[]} */
    let touches = [];
    let pinchStartDist = 0;
    let pinchStartScale = 1;
    let touchDragStart = null;
    let touchDragTranslate = null;
    let touchDragged = false;
    let lastTapAt = 0;
    let lastTapAtPos = null;
    let swipeStart = null;

    stage.addEventListener(
      'touchstart',
      (e) => {
        e.preventDefault();
        stage.classList.add('is-interacting');
        touches = Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY }));
        if (touches.length === 2) {
          pinchStartDist = dist(touches[0], touches[1]);
          pinchStartScale = scale;
        } else if (touches.length === 1) {
          touchDragStart = { x: touches[0].x, y: touches[0].y };
          touchDragTranslate = { x: tx, y: ty };
          touchDragged = false;
          swipeStart = { x: touches[0].x, y: touches[0].y, t: performance.now() };

          // Double-tap detection.
          const now = performance.now();
          if (
            now - lastTapAt < DOUBLE_TAP_MS &&
            lastTapAtPos &&
            dist(lastTapAtPos, touches[0]) < 40
          ) {
            const rect = stage.getBoundingClientRect();
            if (scale > MIN_ZOOM + 0.05) {
              scale = MIN_ZOOM;
              tx = 0;
              ty = 0;
              applyTransform();
            } else {
              zoomTo(DOUBLE_ZOOM, touches[0].x - rect.left, touches[0].y - rect.top);
            }
            lastTapAt = 0;
            lastTapAtPos = null;
            touchDragStart = null;
            swipeStart = null;
            return;
          }
          lastTapAt = now;
          lastTapAtPos = { x: touches[0].x, y: touches[0].y };
        }
      },
      { passive: false }
    );

    stage.addEventListener(
      'touchmove',
      (e) => {
        e.preventDefault();
        const t = Array.from(e.touches).map((t) => ({ x: t.clientX, y: t.clientY }));
        if (t.length === 2) {
          const d = dist(t[0], t[1]);
          const mid = { x: (t[0].x + t[1].x) / 2, y: (t[0].y + t[1].y) / 2 };
          const rect = stage.getBoundingClientRect();
          const newScale = clamp((d / pinchStartDist) * pinchStartScale, MIN_ZOOM, MAX_ZOOM);
          zoomTo(newScale, mid.x - rect.left, mid.y - rect.top);
          touchDragStart = null;
          swipeStart = null;
        } else if (t.length === 1 && touchDragStart) {
          const dx = t[0].x - touchDragStart.x;
          const dy = t[0].y - touchDragStart.y;
          if (!touchDragged && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
          touchDragged = true;
          if (scale > MIN_ZOOM + 0.001) {
            tx = touchDragTranslate.x + dx / scale;
            ty = touchDragTranslate.y + dy / scale;
            constrain();
            applyTransform();
          }
        }
      },
      { passive: false }
    );

    stage.addEventListener(
      'touchend',
      (e) => {
        // Detect horizontal swipe for navigation when not zoomed.
        if (
          swipeStart &&
          scale <= MIN_ZOOM + 0.001 &&
          e.changedTouches.length === 1 &&
          !touchDragged
        ) {
          const t = e.changedTouches[0];
          const dx = t.clientX - swipeStart.x;
          const dy = t.clientY - swipeStart.y;
          if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * 1.2) {
            if (dx < 0) setIndex(currentIndex + 1);
            else setIndex(currentIndex - 1);
          }
        }
        if (e.touches.length === 0) {
          touchDragStart = null;
          touchDragTranslate = null;
          swipeStart = null;
          touchDragged = false;
          stage.classList.remove('is-interacting');
        }
      },
      { passive: false }
    );

    // ---- Sync currentIndex with the underlying gallery -------------------
    // When the user changes the active thumbnail, keep our default-open index in sync.
    // (Reading data-current-index at open time already covers this, so nothing to do here.)
  }

  function init() {
    document.querySelectorAll('section.dw-product').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor).
  document.addEventListener('shopify:section:load', (e) => {
    const sec = e.target?.querySelector?.('section.dw-product') || e.target;
    if (sec instanceof HTMLElement) initSection(sec);
  });
})();
